import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { convertItemsToTilesSeries } from "../formatters/ItemFormatter";
import { fetchData } from "../workers/fetchWorkerManager";
import { dnsResolver } from "./dnsResolver";

// Cache for processed Last Added series
const lastAddedCache = new Map<
  string,
  {
    data: any[];
    timestamp: number;
    cacheKey: string;
    version: number;
  }
>();

// Cache expiry time (24 hours in milliseconds)
const CACHE_EXPIRY = 24 * 60 * 60 * 1000;
const CACHE_VERSION = 1;

// ✅ ADDED: Helper to get current playlist identifier
const getCurrentPlaylistId = () => {
  return `${playListUrl()}_${playListUsername()}`;
};

// Enhanced fetchData wrapper with JSON error handling
const safeFetchData = async (url: string, context: string = "unknown") => {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1000;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const result = await fetchData(url);

      // Validate result is not HTML
      if (typeof result === "string" && result.trim().startsWith("<")) {
        throw new Error(`Received HTML instead of JSON for ${context}`);
      }

      // Ensure result is an array
      if (!Array.isArray(result)) {
        if (result && typeof result === "object") {
          // Try to extract array from object
          if (Array.isArray(result.data)) return result.data;
          if (Array.isArray(result.categories)) return result.categories;
          if (Array.isArray(result.streams)) return result.streams;
          if (Array.isArray(result.series)) return result.series;
        }
        return [];
      }

      return result;
    } catch (error) {
      if (attempt === MAX_RETRIES) {
        return [];
      }
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
    }
  }

  return [];
};

// Helper function to build API URL with DNS support
const buildApiUrl = async (
  baseUrl: string,
  username: string,
  password: string,
  action: string,
  additionalParams: string = "",
): Promise<string> => {
  let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;
  if (additionalParams) {
    apiUrl += `&${additionalParams}`;
  }

  let finalApiUrl = apiUrl;
  try {
    const dnsResult = await dnsResolver.transformUrl(apiUrl);

    if (typeof dnsResult === "object" && dnsResult.url) {
      finalApiUrl = dnsResult.url;
    } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
      finalApiUrl = dnsResult;
    }
  } catch (dnsError) {
    finalApiUrl = apiUrl;
  }

  return finalApiUrl;
};

export default function (filter: string) {
  return async (pageIndex: number): Promise<any> => {
    const username = playListUsername();
    const password = playListPassword();
    const baseUrl = playListUrl();

    // Validate required parameters
    if (!username || !password || !baseUrl) {
      console.error("Missing required parameters for Series Last Added");
      return [];
    }

    // ✅ MODIFIED: Include playlist in cache key
    const currentPlaylist = getCurrentPlaylistId();
    const cacheKey = `${currentPlaylist}_series_last_added_v2`;

    // Check if we have valid cached data
    const cachedData = lastAddedCache.get(cacheKey);
    const now = Date.now();

    if (cachedData && now - cachedData.timestamp < CACHE_EXPIRY && cachedData.version === CACHE_VERSION) {
      console.log("🚀 Using cached Series Last Added data");
      return cachedData.data;
    }

    // Fetch with smart category selection
    try {
      console.log("📡 Fetching Series Last Added with smart category selection...");

      // Step 1: Get series categories
      const categoriesUrl = await buildApiUrl(baseUrl, username, password, "get_series_categories");
      const categories = await safeFetchData(categoriesUrl, "series categories");

      if (!Array.isArray(categories) || categories.length === 0) {
        console.warn("No series categories available for Last Added");
        return [];
      }

      console.log(`📊 Found ${categories.length} series categories`);

      // Step 2: Smart category fetching logic
      const TARGET_SERIES_COUNT = 15;
      const MAX_CATEGORIES_TO_CHECK = 20;
      const MIN_SERIES_PER_CATEGORY = 5;

      let allRecentSeries: any[] = [];
      let categoriesChecked = 0;
      let uniqueSeriesCount = 0;

      console.log(`🎯 Target: ${TARGET_SERIES_COUNT} unique series`);

      // Step 3: Fetch series dynamically until we have enough
      for (const category of categories) {
        if (uniqueSeriesCount >= TARGET_SERIES_COUNT) {
          console.log(
            `✋ Stopping: have ${uniqueSeriesCount} unique series (target: ${TARGET_SERIES_COUNT})`,
          );
          break;
        }

        if (categoriesChecked >= MAX_CATEGORIES_TO_CHECK) {
          console.log(
            `⚠️ Reached max categories limit (${MAX_CATEGORIES_TO_CHECK}), stopping with ${uniqueSeriesCount} series`,
          );
          break;
        }

        try {
          const categoryId = category.category_id;
          const categoryName = category.category_name;

          const categorySeriesUrl = await buildApiUrl(
            baseUrl,
            username,
            password,
            "get_series",
            `category_id=${categoryId}`,
          );

          const categorySeries = await safeFetchData(categorySeriesUrl, `category ${categoryName}`);

          if (Array.isArray(categorySeries) && categorySeries.length > 0) {
            const seriesWithCategory = categorySeries.map(series => ({
              ...series,
              source_category: categoryName,
            }));

            allRecentSeries.push(...seriesWithCategory);

            // Calculate unique series count so far
            const seenNames = new Set<string>();
            const uniqueSoFar = allRecentSeries.filter((s: any) => {
              const name = s.name?.toLowerCase()?.trim();
              if (!name || seenNames.has(name)) return false;
              seenNames.add(name);
              return true;
            });

            uniqueSeriesCount = uniqueSoFar.length;
            categoriesChecked++;

            if (uniqueSeriesCount >= TARGET_SERIES_COUNT) {
              break;
            }

            if (categorySeries.length >= 15 && uniqueSeriesCount >= TARGET_SERIES_COUNT - 5) {
              break;
            }

            if (categorySeries.length < MIN_SERIES_PER_CATEGORY) {
              continue;
            }
          } else {
            categoriesChecked++;
          }
        } catch (error) {
          categoriesChecked++;
        }
      }

      // Step 4: Process and filter series (remove duplicates)
      const seenNames = new Set<string>();
      const seenIds = new Set<string>();

      const uniqueSeries = allRecentSeries
        .filter((series: any) => {
          if (!series || !series.name || !series.series_id) return false;
          return true;
        })
        .filter((series: any) => {
          const seriesName = series.name.toLowerCase().trim();
          const seriesId = String(series.series_id);

          if (seenNames.has(seriesName) || seenIds.has(seriesId)) {
            return false;
          }

          seenNames.add(seriesName);
          seenIds.add(seriesId);
          return true;
        });

      // Step 5: Sort by last_modified and limit to TARGET_SERIES_COUNT
      const sortedSeries = uniqueSeries
        .sort((a: any, b: any) => {
          const modifiedA = parseInt(a.last_modified || "0");
          const modifiedB = parseInt(b.last_modified || "0");
          return modifiedB - modifiedA;
        })
        .slice(0, TARGET_SERIES_COUNT);

      console.log(
        `✅ Series Last Added processed: ${uniqueSeries.length} unique → ${sortedSeries.length} newest (from ${categoriesChecked} categories)`,
      );

      // Step 6: Convert to tiles
      const processedTiles = convertItemsToTilesSeries(sortedSeries);

      // Step 7: Cache the results
      lastAddedCache.set(cacheKey, {
        data: processedTiles,
        timestamp: now,
        cacheKey: cacheKey,
        version: CACHE_VERSION,
      });

      console.log(`💾 Series Last Added cached successfully (${processedTiles.length} tiles)`);

      // Step 8: Return tiles directly
      return processedTiles;
    } catch (error) {
      console.error("❌ Error getting Series Last Added:", error);
      return [];
    }
  };
}

// Export helper functions
export const clearSeriesLastAddedCache = () => {
  lastAddedCache.clear();
  console.log("🗑️ Series Last Added cache cleared");
};

export const clearSeriesLastAddedCacheForPlaylist = (baseUrl: string, username: string, password: string) => {
  const playlistId = `${baseUrl}_${username}`;
  const keysToDelete = Array.from(lastAddedCache.keys()).filter(key => key.startsWith(playlistId));

  keysToDelete.forEach(key => lastAddedCache.delete(key));
  console.log(`🗑️ Cleared ${keysToDelete.length} cache entries for series playlist: ${baseUrl}`);
};

export const getSeriesLastAddedCacheStatus = () => {
  const status = Array.from(lastAddedCache.entries()).map(([key, value]) => ({
    key,
    itemCount: value.data.length,
    age: Date.now() - value.timestamp,
    ageMinutes: Math.floor((Date.now() - value.timestamp) / (1000 * 60)),
    isExpired: Date.now() - value.timestamp > CACHE_EXPIRY,
    version: value.version,
    isValidVersion: value.version === CACHE_VERSION,
  }));

  return status;
};

export const getSeriesLastAddedStats = () => {
  return {
    cacheSize: lastAddedCache.size,
    optimizationMethod: "Smart Dynamic Category Fetching",
    strategy: "Fetch until 15 series, stop early if categories have many series",
    performance: "Fast and adaptive - fetches 2-10 categories based on content",
    dnsSupport: "Enabled - uses custom DNS when configured",
    vpnSupport: "Enabled - handled by fetchWorkerManager",
    errorHandling: "Enhanced - prevents JSON parsing crashes",
  };
};

// Declare window type for TypeScript
declare global {
  interface Window {
    dnsResolver?: any;
  }
}
